import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { Box, Flex, Inline, Pressable, Text, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import Tooltip from '@atlaskit/tooltip';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { type Database } from '@databases/types/database';
import { DatabaseCalculationType } from '@databases/types/database/view';
import { getReferencedJiraIssueDetailsField } from '@databases/utils/database/field-types/jiraIssueDetails';
import { getNumberDisplayString } from '@databases/utils/database/field-types/number';
import {
  fieldHasCalculations,
  getDatabaseFieldRequired,
  getFieldCalculationType,
} from '@databases/utils/database/utils';
import { selectFieldNumberFormat } from '@databases/utils/redux/databases/selectors';
import { selectSupportedProperties } from '@databases/utils/redux/datasources/selectors';
import useView from '@databases/views/useView';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';

import { DatabaseCalculationLabel } from './DatabaseCalculationLabel';
import { QuickCalcOptions } from './QuickCalcOptions';
import { getAllEntriesInField, getCalculationFunction } from './calculations';
import {
  formatPercent,
  getCalculationOptionsForFieldType,
  handleCalculationSelectionChange,
  isPercentCalculation,
  isQuickCalcDisabledFieldTypes,
} from './quickCalcUtils';
import { type CalculationSelectOption } from './types';
import { useLoadJiraFieldDependencies } from './useLoadJiraFieldDependencies';

const BUTTON_MARGIN = 14 + 14; // space on each side of the button
const CALCULATION_TYPES_WITHOUT_FORMATTING = [
  DatabaseCalculationType.COUNT_TOTAL,
  DatabaseCalculationType.COUNT,
  DatabaseCalculationType.COUNT_UNIQUE,
  DatabaseCalculationType.COUNT_EMPTY,
];

const buttonContainerStyles = xcss({
  width: '100%',
  paddingTop: 'space.075',
  paddingLeft: 'space.025',
  paddingRight: 'space.025',
  paddingBottom: 'space.075',
});

const compactButtonContainerStyles = xcss({
  paddingTop: 'space.050',
  paddingBottom: 'space.050',
});

const calculationLabelInlineStyles = xcss({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  flexShrink: 0,
});

const getCalculationResultsContainerStyles = (hideCalculationLabel: boolean) => {
  return xcss({
    overflowWrap: 'unset',
    // @remark: Disabled as part of https://bitbucket.org/atlassian/canvas/pull-requests/27504/overview,
    // this should be re-enabled as soon as feasible
    // eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
    fontWeight: 'bold',
    textOverflow: hideCalculationLabel ? 'ellipsis' : 'unset',
    overflow: 'hidden',
  });
};

interface QuickCalculationsSelectProps {
  compact?: boolean;
  fieldId: string;
  database: Database;
  width: number | undefined;
  canEditContent: boolean;
  isFirstField: boolean;
}

const QuickCalculationsSelect: FunctionComponent<QuickCalculationsSelectProps> = ({
  compact,
  fieldId,
  database,
  width,
  canEditContent,
  isFirstField,
}) => {
  const { view } = useView();
  const dispatch = defaultStoreUtils.useDispatch();
  const { formatMessage, locale } = useIntl();

  const store = defaultStoreUtils.useStore();
  const analyticsClient = useAnalytics();

  const [isOpen, setIsOpen] = useState(false);
  const [isButtonVisible, setIsButtonVisible] = useState(false);
  const [hideCalculationLabel, setHideCalculationLabel] = useState(false);
  const [savedLabelWidth, setSavedLabelWidth] = useState<number>(0);
  const [showTooltip, setShowTooltip] = useState(false);

  const calculationResultRef = useRef<HTMLSpanElement>(null);
  const calculationLabelRef = useRef<HTMLSpanElement>(null);
  const chevronRef = useRef<HTMLSpanElement>(null);
  const buttonContainerRef = useRef<HTMLDivElement>(null);

  const [numberFormat, field, entries, jiraFieldProperties] =
    defaultStoreUtils.useEqualSelector((state) => {
      const numberFormat = selectFieldNumberFormat(state, { database, fieldId });
      const field = getDatabaseFieldRequired(database, fieldId);
      const entries = getAllEntriesInField(database, fieldId, state);

      const referencedJiraField = getReferencedJiraIssueDetailsField({ database, field });
      const cloudId = referencedJiraField?.settings.cloudId;
      const jiraFieldProperties = cloudId
        ? selectSupportedProperties(state, cloudId, 'jira')
        : undefined;

      return [numberFormat, field, entries, jiraFieldProperties] as const;
    });

  const databaseContainer = getDatabaseContainerType();
  const calculationType = view.settings.calculations?.[fieldId];

  const hasCompatibleCalculation = useMemo(() => {
    const allowedCalculations = getCalculationOptionsForFieldType(
      field,
      store.getState(),
      database.contentId,
      dispatch
    );
    return calculationType && allowedCalculations.includes(calculationType);
    // jiraFieldProperties triggers re-computation when Jira field schema loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculationType, field, store, database.contentId, dispatch, jiraFieldProperties]);

  const { isLoading: isLoadingJiraDependencies } = useLoadJiraFieldDependencies(
    database,
    fieldId,
    calculationType
  );

  const handleChange = useCallback(
    (option: CalculationSelectOption | null) => {
      handleCalculationSelectionChange(
        option,
        database.contentId,
        dispatch,
        fieldId,
        view.id
      );
    },
    [database.contentId, dispatch, fieldId, view.id]
  );

  const pressableStyles = useMemo(() => {
    return xcss({
      width: '100%',
      backgroundColor: 'color.background.input.hovered',
      borderRadius: 'radius.small',
      paddingTop: 'space.075',
      paddingBottom: 'space.075',
      // if changing left or right padding, it must be updated in the resizing
      // logic to avoid ui bugs.
      paddingLeft: 'space.150',
      paddingRight: 'space.150',
      position: 'relative',
      maxHeight: '36px',
      visibility:
        hasCompatibleCalculation || isButtonVisible || (isFirstField && canEditContent)
          ? 'visible'
          : 'hidden',
    });
  }, [canEditContent, isButtonVisible, isFirstField, hasCompatibleCalculation]);

  const handleOnClick = useCallback(
    (databaseCalculationType?: DatabaseCalculationType) => {
      if (databaseCalculationType) {
        handleChange({ value: databaseCalculationType, label: databaseCalculationType });

        if (calculationType && calculationType !== databaseCalculationType) {
          analyticsClient.sendCalculationEventConverted({
            databaseContainer,
            fieldId: field.id,
            fieldType: field.type,
            fromCalculationType: calculationType,
            toCalculationType: databaseCalculationType,
            source: 'quickCalculationSelect',
            contentId: database.contentId,
          });
        } else {
          analyticsClient.sendCreateCalculationEvent({
            databaseContainer,
            fieldId: field.id,
            fieldType: field.type,
            calculationType: databaseCalculationType,
            source: 'quickCalculationSelect',
            contentId: database.contentId,
          });
        }
      } else {
        handleChange(null);
        analyticsClient.sendRemoveCalculationEvent({
          databaseContainer,
          fieldId: field.id,
          fieldType: field.type,
          source: 'quickCalculationSelect',
          contentId: database.contentId,
        });
      }
      setIsOpen(false);
    },
    [
      analyticsClient,
      calculationType,
      database.contentId,
      databaseContainer,
      field.id,
      field.type,
      handleChange,
    ]
  );

  const handleMouseEnter = useCallback(() => {
    if (!hasCompatibleCalculation && canEditContent) {
      setIsButtonVisible(true);
    }
  }, [hasCompatibleCalculation, canEditContent]);

  const handleMouseLeave = useCallback(() => {
    if (!hasCompatibleCalculation && !isOpen) {
      setIsButtonVisible(false);
      setIsOpen(false);
    }
  }, [hasCompatibleCalculation, isOpen]);

  const handleOnClose = useCallback(() => {
    setIsOpen(false);
    setIsButtonVisible(false);
  }, []);

  const calculationResult = useMemo(() => {
    if (!calculationType || !numberFormat || !hasCompatibleCalculation) {
      return undefined;
    }

    if (isLoadingJiraDependencies) {
      return null;
    }

    const calculationFunction = getCalculationFunction(calculationType);

    const preformattedResult = calculationFunction(
      database.contentId,
      fieldId,
      entries,
      store
    );

    if (preformattedResult === null) {
      return '-';
    }

    const formattedResult = getNumberDisplayString(
      preformattedResult.toString(),
      field,
      locale
    );

    if (CALCULATION_TYPES_WITHOUT_FORMATTING.includes(calculationType)) {
      return preformattedResult;
    } else if (isPercentCalculation(calculationType)) {
      return formatPercent(calculationType, preformattedResult.toString());
    } else {
      return formattedResult;
    }
  }, [
    calculationType,
    numberFormat,
    database.contentId,
    fieldId,
    entries,
    store,
    field,
    locale,
    hasCompatibleCalculation,
    isLoadingJiraDependencies,
  ]);

  const getTooltipContent = useCallback(() => {
    if (isQuickCalcDisabledFieldTypes(field.type)) {
      return formatMessage(messages['quick.calculations.field-disabled-text']);
    }
    if (showTooltip) {
      return calculationResult;
    } else {
      return '';
    }
  }, [calculationResult, field.type, formatMessage, showTooltip]);

  useEffect(() => {
    const buttonContainerWidth = buttonContainerRef.current?.offsetWidth || 0;
    const labelWidth = calculationLabelRef.current?.offsetWidth || 0;
    const chevronWidth = chevronRef.current?.offsetWidth || 0;
    const calculationResultWidth = calculationResultRef.current?.offsetWidth || 0;

    const contentWidth =
      labelWidth + chevronWidth + calculationResultWidth + BUTTON_MARGIN + 2; // 2px added for buffer
    const gap = buttonContainerWidth - contentWidth;

    if (!showTooltip && gap <= 0) {
      setShowTooltip(true);
    } else if (showTooltip && gap > 0) {
      setShowTooltip(false);
    }

    if (!hideCalculationLabel) {
      setSavedLabelWidth(labelWidth);
    }

    if (hideCalculationLabel && gap - savedLabelWidth > 0) {
      setHideCalculationLabel(false);
    }

    if (!hideCalculationLabel && contentWidth >= buttonContainerWidth) {
      setHideCalculationLabel(true);
    }
  }, [
    calculationResult,
    field.name,
    fieldId,
    hideCalculationLabel,
    savedLabelWidth,
    showTooltip,
    width,
  ]);

  useEffect(() => {
    if (!fieldHasCalculations(fieldId, view.settings.calculations)) {
      setIsButtonVisible(false);
    }
  }, [fieldId, view.settings.calculations]);

  const popupContent = useCallback(
    () => (
      <Box xcss={xcss({ maxHeight: '300px' })}>
        <QuickCalcOptions
          field={field}
          handleOnClick={handleOnClick}
          calculations={view.settings.calculations}
          contentId={database.contentId}
        />
      </Box>
    ),
    [field, handleOnClick, view.settings.calculations, database.contentId]
  );

  const SelectButton = useCallback(
    (triggerProps: TriggerProps) => {
      return (
        <Pressable
          isDisabled={isQuickCalcDisabledFieldTypes(field.type)}
          {...triggerProps}
          onClick={() => {
            if (canEditContent) {
              setIsOpen(!isOpen);
            }
          }}
          xcss={pressableStyles}
        >
          <Inline spread="space-between" alignBlock="center">
            <Inline alignBlock="center" xcss={calculationLabelInlineStyles}>
              {!hideCalculationLabel && (
                <Text weight="semibold" ref={calculationLabelRef} maxLines={1}>
                  <DatabaseCalculationLabel
                    type={getFieldCalculationType(fieldId, view.settings.calculations)}
                    fieldType={field.type}
                    useBoldText={true}
                    hasCompatibleCalculation={hasCompatibleCalculation}
                  />
                </Text>
              )}
              {canEditContent && (
                <Inline ref={chevronRef}>
                  <Inline>
                    <ChevronDownIcon spacing="spacious" label="" />
                  </Inline>
                </Inline>
              )}
            </Inline>
            <Inline
              alignBlock="center"
              xcss={xcss({
                overflow: 'hidden',
                overflowWrap: 'unset',
              })}
            >
              {isQuickCalcDisabledFieldTypes(field.type) ? (
                <StatusInformationIcon spacing="spacious" label={''} />
              ) : isLoadingJiraDependencies ? (
                <Spinner size="small" />
              ) : (
                <Box
                  as="span"
                  color="color.text"
                  ref={calculationResultRef}
                  xcss={getCalculationResultsContainerStyles(hideCalculationLabel)}
                  testId="quick-calculation-result"
                >
                  {calculationResult}
                </Box>
              )}
            </Inline>
          </Inline>
        </Pressable>
      );
    },
    [
      calculationResult,
      canEditContent,
      field.type,
      fieldId,
      hideCalculationLabel,
      isLoadingJiraDependencies,
      isOpen,
      pressableStyles,
      view.settings.calculations,
      hasCompatibleCalculation,
    ]
  );

  const triggerComponent = useCallback(
    (triggerProps: TriggerProps) => {
      return (
        <Flex xcss={xcss({ width: '100%' })}>
          <Box
            {...triggerProps}
            ref={buttonContainerRef}
            xcss={[buttonContainerStyles, compact && compactButtonContainerStyles]}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <Tooltip content={getTooltipContent()} position="top">
              {(tooltipProps) => (
                <Box {...tooltipProps}>{SelectButton(triggerProps)}</Box>
              )}
            </Tooltip>
          </Box>
        </Flex>
      );
    },
    [
      compact,
      getTooltipContent,
      handleMouseEnter,
      handleMouseLeave,
      SelectButton,
      buttonContainerRef,
    ]
  );

  return (
    <Popup
      isOpen={isOpen}
      onClose={handleOnClose}
      placement="top-start"
      content={popupContent}
      trigger={triggerComponent}
    />
  );
};

export default QuickCalculationsSelect;
